import React, { useState } from 'react';
import { Card, Badge } from '@tremor/react';
import { Trophy, ChevronUp, ChevronDown, MapPin } from 'lucide-react';

export interface SubmarketComparisonData {
    name: string;
    rank: number;
    compositeScore: number;
    rentGapScore: number;
    momentumScore: number;
}

interface SubmarketComparisonTableProps {
    currentSubmarket: string;
    submarkets: SubmarketComparisonData[];
}

type SortKey = 'rank' | 'compositeScore' | 'rentGapScore' | 'momentumScore';

/**
 * SubmarketComparisonTable - Shows how the property's submarket ranks against others.
 *
 * Features:
 * - Top 5 submarkets by composite score displayed
 * - Current submarket highlighted with blue background
 * - Medal icons for top 3 (gold/silver/bronze)
 * - Sortable columns
 * - "Your Property" badge on current submarket row
 */
export const SubmarketComparisonTable: React.FC<SubmarketComparisonTableProps> = ({
    currentSubmarket,
    submarkets,
}) => {
    const [sortKey, setSortKey] = useState<SortKey>('rank');
    const [sortAsc, setSortAsc] = useState<boolean>(true);

    // Handle column header click for sorting
    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            // Default sort direction: rank ascending, scores descending
            setSortAsc(key === 'rank');
        }
    };

    // Sort submarkets
    const sortedSubmarkets = [...submarkets].sort((a, b) => {
        const multiplier = sortAsc ? 1 : -1;
        return (a[sortKey] - b[sortKey]) * multiplier;
    });

    // Get top 5 submarkets
    const top5 = sortedSubmarkets.slice(0, 5);

    // Check if current submarket is in top 5
    const currentInTop5 = top5.some(
        (s) => s.name.toLowerCase() === currentSubmarket.toLowerCase()
    );

    // If current submarket is not in top 5, add it at the bottom
    const currentSubmarketData = submarkets.find(
        (s) => s.name.toLowerCase() === currentSubmarket.toLowerCase()
    );

    // Medal colors for top 3
    const getMedalColor = (rank: number): string | null => {
        switch (rank) {
            case 1:
                return '#FFD700'; // Gold
            case 2:
                return '#C0C0C0'; // Silver
            case 3:
                return '#CD7F32'; // Bronze
            default:
                return null;
        }
    };

    // Sort indicator
    const SortIndicator = ({ columnKey }: { columnKey: SortKey }) => {
        if (sortKey !== columnKey) return null;
        return sortAsc ? (
            <ChevronUp className="w-3 h-3 inline-block ml-1" />
        ) : (
            <ChevronDown className="w-3 h-3 inline-block ml-1" />
        );
    };

    // Render a table row
    const renderRow = (submarket: SubmarketComparisonData, isCurrentSubmarket: boolean, showDivider: boolean = false) => {
        const medalColor = getMedalColor(submarket.rank);
        const rowClasses = isCurrentSubmarket
            ? 'bg-blue-50 border-l-4 border-l-blue-500'
            : 'hover:bg-gray-50';

        return (
            <React.Fragment key={submarket.name}>
                {showDivider && (
                    <tr>
                        <td colSpan={5} className="py-1 text-center text-gray-400 text-sm">
                            ...
                        </td>
                    </tr>
                )}
                <tr className={rowClasses}>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                            {medalColor ? (
                                <Trophy
                                    className="w-4 h-4"
                                    style={{ color: medalColor }}
                                    fill={medalColor}
                                />
                            ) : (
                                <span className="w-4 text-center text-gray-500">
                                    {submarket.rank}
                                </span>
                            )}
                        </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                            {submarket.name}
                            {isCurrentSubmarket && (
                                <Badge size="xs" color="blue" icon={MapPin}>
                                    Your Property
                                </Badge>
                            )}
                        </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 text-center">
                        <span
                            className={`font-semibold ${
                                submarket.compositeScore >= 60
                                    ? 'text-green-600'
                                    : submarket.compositeScore >= 40
                                    ? 'text-yellow-600'
                                    : 'text-red-600'
                            }`}
                        >
                            {submarket.compositeScore.toFixed(1)}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600 text-center">
                        {submarket.rentGapScore.toFixed(0)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600 text-center">
                        {submarket.momentumScore.toFixed(0)}
                    </td>
                </tr>
            </React.Fragment>
        );
    };

    if (submarkets.length === 0) {
        return (
            <Card>
                <h3 className="text-tremor-title font-medium text-tremor-content-strong">
                    Submarket Comparison
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                    No submarket data available for comparison.
                </p>
            </Card>
        );
    }

    return (
        <Card>
            <div className="mb-4">
                <h3 className="text-tremor-title font-medium text-tremor-content-strong">
                    Submarket Comparison
                </h3>
                <p className="mt-1 text-tremor-default text-tremor-content">
                    How <span className="font-semibold">{currentSubmarket}</span> stacks up
                </p>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th
                                className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('rank')}
                            >
                                Rank
                                <SortIndicator columnKey="rank" />
                            </th>
                            <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                                Submarket
                            </th>
                            <th
                                className="px-3 py-2 text-center text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('compositeScore')}
                            >
                                Composite
                                <SortIndicator columnKey="compositeScore" />
                            </th>
                            <th
                                className="px-3 py-2 text-center text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('rentGapScore')}
                            >
                                Rent Gap
                                <SortIndicator columnKey="rentGapScore" />
                            </th>
                            <th
                                className="px-3 py-2 text-center text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('momentumScore')}
                            >
                                Momentum
                                <SortIndicator columnKey="momentumScore" />
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {top5.map((submarket) =>
                            renderRow(
                                submarket,
                                submarket.name.toLowerCase() === currentSubmarket.toLowerCase()
                            )
                        )}
                        {!currentInTop5 && currentSubmarketData && (
                            renderRow(currentSubmarketData, true, true)
                        )}
                    </tbody>
                </table>
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                    <Trophy className="w-3 h-3" style={{ color: '#FFD700' }} fill="#FFD700" />
                    <span>Top 3 by composite score</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-100 border-l-2 border-blue-500"></div>
                    <span>Your property's submarket</span>
                </div>
            </div>
        </Card>
    );
};

export default SubmarketComparisonTable;
